/**
 * @store/notifications — Central in-app notification queue.
 *
 * Distinct from sonner toasts (which are fire-and-forget for transient
 * feedback). This store holds persistent notifications that should appear
 * in a badge / drawer until the user dismisses them — e.g. "Plan expires
 * in 3 days", "New lead assigned", "Property approved".
 *
 * Server-pushed notifications hydrate this store on mount; client-fired
 * notifications (e.g. optimistic "Saved to shortlist") use `push`.
 */

import { create } from "zustand";

/**
 * Shape of one notification:
 *   {
 *     id: string,           // uuid or server id
 *     kind: "info"|"warn"|"error"|"success",
 *     title: string,
 *     body?: string,
 *     href?: string,        // optional click-target
 *     createdAt: number,
 *     read: boolean,
 *   }
 */

export const useNotificationsStore = create((set, get) => ({
  items: [],

  /** Replace the entire queue (used after server hydrate). */
  hydrate: (items) => set({ items: items || [] }),

  /** Add a new notification at the top. */
  push: (n) => {
    if (!n?.id) return;
    const exists = get().items.some((x) => x.id === n.id);
    if (exists) return;
    set({
      items: [
        { kind: "info", read: false, createdAt: Date.now(), ...n },
        ...get().items,
      ],
    });
  },

  /** Mark one as read. */
  markRead: (id) =>
    set({
      items: get().items.map((x) => (x.id === id ? { ...x, read: true } : x)),
    }),

  /** Mark all as read. */
  markAllRead: () =>
    set({ items: get().items.map((x) => ({ ...x, read: true })) }),

  /** Remove one notification. */
  dismiss: (id) => set({ items: get().items.filter((x) => x.id !== id) }),

  /** Empty everything. */
  clear: () => set({ items: [] }),
}));

/* Selectors */
export const useNotifications = () =>
  useNotificationsStore((s) => s.items);

export const useUnreadCount = () =>
  useNotificationsStore((s) => s.items.filter((x) => !x.read).length);
